import React, {useEffect} from 'react';
import taxActions from '../../../../actions/tax.actions';
import trackingAction, {EVENT_ACTION, EVENT_CATEGORY} from '../../../../actions/tracking.actions';
import Typography from '@material-ui/core/Typography';
import Message from '../../../../components/common/Message';
import ConfirmDialog from '../../../../components/common/ConfirmDialog';
import BusinessType from '../../../../constants/BusinessType';
import RadioGroup from '../../../../components/v2/form/RadioGroup';
import {connect} from 'react-redux';
import {getLocale} from 'epic-common/src/locale';
import {useFormEnableSubmit, useGetMessages} from '../../../../hooks';
import {selectTax} from '../../../../selectors';
import {SubmitHandler} from 'react-hook-form';
import {withRHFormProvider} from '../../../../hocs';
import {Input} from '../../../../components/v2';
import {EMAIL_REGEX} from '../../../../../shared/constants/regex';
import {OrganizationBox} from './TaxRecordDialog.styles';

const mapStateToProps = state => ({
    taxState: selectTax(state)
});

const mapDispatchToProps = {
    getTaxInterviewURL: taxActions.getTaxInterviewURL,
    retakeInterview: taxActions.retakeInterview,
    resetDialog: taxActions.resetDialog,
    trackClickEvent: trackingAction.trackClickEvent,
    getRecordEmail: taxActions.getRecordEmail
};

export interface TaxRecordDialogProps {
    open: boolean;
    onClose: () => void;
}

export interface TaxRecordDialogFieldValues {
    profileType: BusinessType;
    organizationId?: string;
    organizationEmail?: string;
}

type DispatchProps = typeof mapDispatchToProps;
type StateProps = ReturnType<typeof mapStateToProps>;
type EnhancedProps = TaxRecordDialogProps & DispatchProps & StateProps;

const TaxRecordDialog = withRHFormProvider<EnhancedProps, TaxRecordDialogFieldValues>(
    ({
        open,
        onClose,
        taxState,
        getTaxInterviewURL,
        retakeInterview,
        resetDialog,
        trackClickEvent,
        getRecordEmail,
        methods
    }) => {
        const watchProfileType = methods.watch('profileType');
        const isOrg = watchProfileType === BusinessType.OTHER_ENTITY;
        const enableSubmitButton = useFormEnableSubmit();
        const enableButton = !taxState.loading && enableSubmitButton;
        const messages = useGetMessages({
            organizationTitle: 'epic.affiliate.settings.tax.profile.dialog.organization.title',
            organizationEmail: 'ui.profile.tax.organizationEmail',
            single: 'epic.affiliate.settings.tax.profile.dialog.record.single.text',
            sole: 'epic.affiliate.settings.tax.profile.dialog.record.sole.text',
            singleLLC: 'epic.affiliate.settings.tax.profile.dialog.record.single_llc.text',
            otherEntity: 'epic.affiliate.settings.tax.profile.dialog.record.other.text',
            required: 'errors.com.epicgames.validation.required_field.v2',
            invalid: {
                code: 'errors.com.epicgames.validation.invalid',
                args: ['email']
            }
        });

        const handleFetchRecordEmail = () => {
            if (taxState.organizations && taxState.organizations.length) {
                const organizationId = taxState.organizations[0].organizationId;
                methods.setValue('organizationId', organizationId);
                getRecordEmail(organizationId);
            }
        };

        useEffect(() => {
            handleFetchRecordEmail();
        }, [taxState.organizations]);

        useEffect(() => {
            if (taxState.taxInterviewUrl && methods.formState.isSubmitted) {
                onClose();
                return;
            }

            if (taxState.organizationEmail) {
                methods.setValue('organizationEmail', taxState.organizationEmail);
            }
        }, [taxState.organizationEmail, taxState.taxInterviewUrl]);

        const onCancel = () => {
            resetDialog();
            onClose();
        };

        const onSubmit: SubmitHandler<TaxRecordDialogFieldValues> = data => {
            const isOrg = data.profileType === BusinessType.OTHER_ENTITY;
            const postData = {...data, checkAvailibility: true, locale: getLocale()};

            if (!isOrg) {
                delete postData.organizationEmail;
                delete postData.organizationId;
            }

            if (!taxState.submitted) {
                trackClickEvent(EVENT_CATEGORY.TaxIdentity, EVENT_ACTION.ClickTakeTaxIdLink);
                getTaxInterviewURL(postData);
            } else {
                postData.checkAvailibility = taxState.record.organizationEntity ? isOrg : !isOrg;
                trackClickEvent(EVENT_CATEGORY.TaxIdentity, EVENT_ACTION.ClickRetakeTaxIdLink);
                retakeInterview(postData);
            }
        };

        return (
            <ConfirmDialog
                fullWidth
                maxWidth="lg"
                open={open}
                title="epic.affiliate.settings.tax.profile.dialog.title"
                submit="epic.affiliate.common.continue"
                onSubmit={methods.handleSubmit(onSubmit)}
                onClose={onCancel}
                onCancel={onCancel}
                boxProps={{component: 'form'}}
                dialogContentTextProps={{style: {margin: 0}}}
                submitButtonProps={{disabled: !enableButton, loading: taxState.loading}}>
                <RadioGroup
                    required
                    name="profileType"
                    defaultValue={BusinessType.INDIVIDUAL}
                    control={methods.control}
                    rules={{required: {value: true, message: messages.required}}}
                    options={[
                        {
                            label: messages.single,
                            value: BusinessType.INDIVIDUAL
                        },
                        {
                            label: messages.sole,
                            value: BusinessType.SOLE_PROPRIETOR
                        },
                        {
                            label: messages.singleLLC,
                            value: BusinessType.SINGLE_MEMBER_LLC
                        },
                        {
                            label: messages.otherEntity,
                            value: BusinessType.OTHER_ENTITY
                        }
                    ]}
                />

                {isOrg && (
                    <OrganizationBox>
                        <Typography variant="h4" color="textPrimary">
                            {messages.organizationTitle}
                        </Typography>

                        {taxState.organizations && !!taxState.organizations.length && (
                            <RadioGroup
                                required
                                name="organizationId"
                                control={methods.control}
                                rules={{required: {value: true, message: messages.required}}}
                                options={taxState.organizations.map(org => ({
                                    label: <Message code={org.companyName} />,
                                    value: org.organizationId
                                }))}
                            />
                        )}

                        <Input
                            required
                            fullWidth
                            autoComplete="off"
                            type="text"
                            name="organizationEmail"
                            label={messages.organizationEmail}
                            control={methods.control}
                            rules={{
                                required: {value: true, message: messages.required},
                                pattern: {value: EMAIL_REGEX, message: messages.invalid}
                            }}
                        />
                    </OrganizationBox>
                )}
            </ConfirmDialog>
        );
    },
    {
        mode: 'onTouched',
        shouldUnregister: true
    }
);

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TaxRecordDialog);
